"use client";

import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { getRepCoachingBrief } from "@/server-actions/reports/manager-dashboard";

/**
 * Self-contained "Prep for 1:1" coaching brief. Owns its own fetch/loading/error
 * state so it can be dropped anywhere a manager wants a brief (manager dashboard
 * expanded row, person detail page). Mirrors PatternSummaryWidget's pattern.
 */
export default function CoachingBriefWidget({
  userId,
  userName,
  workspaceId,
  timeframeDays,
  dateRange,
}) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);

  // dateRange carries either { timeframeDays } or { startDate, endDate, timezone }
  const range = dateRange || { timeframeDays };

  const loadBrief = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const result = await getRepCoachingBrief({
        userId,
        userName,
        workspaceId,
        ...range,
      });
      if (result.success && result.brief) {
        setBrief(result.brief);
      } else {
        setBrief({ error: true });
      }
    } catch (err) {
      console.error("Failed to load coaching brief:", err);
      setBrief({ error: true });
    } finally {
      setLoading(false);
    }
  }, [loading, userId, userName, workspaceId, timeframeDays, dateRange]);

  if (!brief && !loading) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={loadBrief}
        className="border-premium-200 text-premium-700 hover:bg-premium-50 hover:text-premium-800"
      >
        Prep for 1:1
      </Button>
    );
  }

  if (loading) {
    return (
      <div className="w-full flex items-center gap-2 text-sm text-gray-500 py-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        Generating coaching brief...
      </div>
    );
  }

  if (brief?.error) {
    return (
      <p className="w-full text-sm text-red-600 py-1">
        Failed to load coaching brief.
        <button
          onClick={() => {
            setBrief(null);
            loadBrief();
          }}
          className="ml-2 text-action-600 hover:underline"
        >
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="w-full bg-gradient-to-r from-premium-50 to-action-50 border border-premium-200 rounded-lg p-4 space-y-3">
      <h4 className="text-sm font-semibold text-premium-800">
        1:1 Coaching Prep
      </h4>

      {brief.priorityArea && (
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Priority Area
          </span>
          <p className="text-sm font-semibold text-gray-900 mt-0.5">
            {brief.priorityArea}
          </p>
        </div>
      )}

      {brief.keyPoints?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Key Points
          </span>
          <ul className="mt-1 space-y-1">
            {brief.keyPoints.map((point, i) => (
              <li
                key={i}
                className="text-sm text-gray-700 flex items-start gap-2"
              >
                <span className="text-premium-400 mt-1">&bull;</span>
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.suggestedQuestions?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Suggested Questions
          </span>
          <ul className="mt-1 space-y-1">
            {brief.suggestedQuestions.map((q, i) => (
              <li
                key={i}
                className="text-sm text-gray-600 italic flex items-start gap-2"
              >
                <span className="text-premium-400 mt-1 not-italic">&bull;</span>
                {q}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
